import {useEffect, useMemo, useRef, useState} from 'react';

import {zodResolver} from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {oneOfType, node, func} from 'prop-types';
import {useForm} from 'react-hook-form';

import {defaultValues} from '../components/emmai-forms/forms-components/forms/EmmaiPractitionerForm/helpers/defaultValues';
import practitionerSchema from '../components/emmai-forms/forms-components/forms/EmmaiPractitionerForm/helpers/schema';
import serializePractitioner from '../components/emmai-forms/forms-components/forms/EmmaiPractitionerForm/helpers/serializePractitioner';
import usePractitionerNavigation from '../components/emmai-forms/forms-components/shared/usePractitionerNavigation/usePractitionerNavigation';
import {API_ENDPOINTS, EMMAI_FORMS as FORMS, SNACKBAR_ACTIONS} from '../const';
import EmmaiContext from '../contexts/EmmaiContext';
import useHttp from '../hooks/misc/useHttp';
import useProjects from '../hooks/providers/useProjects';
import useReport from '../hooks/providers/useReport';
import useSnackbar from '../hooks/providers/useSnackbar';

// eslint-disable-next-line complexity
const EmmaiProvider = ({children}) => {
  const startOfMonth = d => new Date(d.getFullYear(), d.getMonth(), 1);
  const today = startOfMonth(new Date());

  const [emmaiData, setEmmaiData] = useState({});
  const [monthlyRestitutionModalOpen, setMonthlyRestitutionModalOpen] = useState(false);
  const [selectedPractitionerId, setSelectedPractitionerId] = useState(null);
  const [isPractitionerUpdating, setIsPractitionerUpdating] = useState(false);

  const selectedPractitioner = (emmaiData.practitioners || []).find(p => p.practitioner_id === selectedPractitionerId);

  const [pendingDate, setPendingDate] = useState(null);
  const [emmaiRestitutionSelectedDate, setEmmaiRestitutionSelectedDate] = useState(today);
  const [warningActivityDataWillBeLostOnMonthChangeModalOpen, setWarningActivityDataWillBeLostOnMonthChangeModalOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState(FORMS.OFFICE_AND_PRACTITIONER_ACTIVITY);

  const {selectedReport, selectedProject} = useReport();
  const {showSnackbar, closeSnackbar} = useSnackbar();
  const {_post} = useHttp();

  const {goToPractitionerActivity, goToAddPractitioner} = usePractitionerNavigation({
    setCurrentForm,
    setIsPractitionerUpdating
  });

  // We'll store a callback reference that form components can register
  const beforeDateChangeRef = useRef(null);

  const practitionerFormMethods = useForm({
    defaultValues,
    resolver: zodResolver(practitionerSchema),
    mode: 'all'
  });

  const registerBeforeDateChange = callback => {
    beforeDateChangeRef.current = callback;
  };

  const proceedMonthChange = async newDate => {
    if (beforeDateChangeRef.current) {
      const canProceed = await beforeDateChangeRef.current(newDate);
      if (!canProceed) return;
    }
    setEmmaiRestitutionSelectedDate(newDate);
  };

  const addProviderPractitioner = newPractitioner => {
    setEmmaiData(currentData => {
      return {
        ...currentData,
        practitioners: [...currentData.practitioners, newPractitioner]
      };
    });
  };

  const updateProviderPractitioner = updatedPractitioner => {
    setEmmaiData(currentData => {
      return {
        ...currentData,
        practitioners: currentData.practitioners.map(p => (p.practitioner_id === updatedPractitioner.practitioner_id ? updatedPractitioner : p))
      };
    });
  };

  const deleteProviderPractitioner = practitionerId => {
    setEmmaiData(currentData => {
      return {
        ...currentData,
        practitioners: currentData.practitioners.filter(p => p.practitioner_id !== practitionerId)
      };
    });
  };

  const getEmmaiData = async siren => {
    try {
      const url = API_ENDPOINTS.emmai.getData;

      const {response, responseJson: data} = await _post(url, {
        siren
      });

      if (response.status === 200) {
        setEmmaiData(data);
        return {
          success: true,
          status: 200,
          data
        };
      }
      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const createPractitioner = async newPractitioner => {
    try {
      const url = API_ENDPOINTS.emmai.createPractitioner;

      const snackbarId = showSnackbar(SNACKBAR_ACTIONS.CREATE_PRACTITIONER, {
        severity: 'info',
        autoHide: false
      });

      const {response, responseJson: data} = await _post(url, [
        {
          ...newPractitioner,
          is_active: 1
        }
      ]);
      console.log({response, data});

      if (response.status === 200) {
        practitionerFormMethods.reset(defaultValues);

        const newPractitionerId = data[0].practitioner_id.toUpperCase();
        addProviderPractitioner({
          ...newPractitioner,
          practitioner_id: newPractitionerId
        });

        closeSnackbar(snackbarId);
        showSnackbar(SNACKBAR_ACTIONS.CREATE_PRACTITIONER_SUCCESS);

        return {
          success: true,
          status: 200,
          practitioner_id: newPractitionerId
        };
      }

      closeSnackbar(snackbarId);

      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const updatePractitioner = async updatedPractitioner => {
    try {
      const url = API_ENDPOINTS.emmai.updatePractitioner;

      const snackbarId = showSnackbar(SNACKBAR_ACTIONS.UPDATE_PRACTITIONER, {
        severity: 'info',
        autoHide: false
      });

      const {response, responseJson: data} = await _post(url, [updatedPractitioner]);

      if (response.status === 200) {
        updateProviderPractitioner(updatedPractitioner);
        closeSnackbar(snackbarId);

        showSnackbar(SNACKBAR_ACTIONS.UPDATE_PRACTITIONER_SUCCESS);
        return {
          success: true,
          status: 200,
          practitioner_id: updatedPractitioner.practitioner_id
        };
      }

      closeSnackbar(snackbarId);

      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const deletePractitioner = async practitionerId => {
    try {
      const url = API_ENDPOINTS.emmai.deletePractitioner;

      const snackbarId = showSnackbar(SNACKBAR_ACTIONS.DELETE_PRACTITIONER, {
        severity: 'info',
        autoHide: false
      });

      const {response, responseJson: data} = await _post(url, {
        practitioner_id: practitionerId
      });

      if (response.status === 200) {
        if (emmaiData.practitioners.length === 1) {
          setSelectedPractitionerId(null);
          goToAddPractitioner();
        } else {
          const firstPractitionerId = emmaiData.practitioners[0].practitioner_id === practitionerId ? emmaiData.practitioners[1].practitioner_id : emmaiData.practitioners[0].practitioner_id;
          setSelectedPractitionerId(firstPractitionerId);
          goToPractitionerActivity();
        }

        deleteProviderPractitioner(practitionerId);
        closeSnackbar(snackbarId);

        showSnackbar(SNACKBAR_ACTIONS.DELETE_PRACTITIONER_SUCCESS);
        return {
          success: true,
          status: 200,
          data
        };
      }

      closeSnackbar(snackbarId);

      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const addActivities = async newActivitiesData => {
    try {
      const url = API_ENDPOINTS.emmai.addActivities;

      const snackbarId = showSnackbar(SNACKBAR_ACTIONS.ADD_PRACTITIONER_ACTIVITIES, {
        severity: 'info',
        autoHide: false
      });

      const {response, responseJson: data} = await _post(url, {
        dental_clinic_info: newActivitiesData.dentalOfficeInfo,
        practitioner_activities: newActivitiesData.practitionerActivities
      });

      closeSnackbar(snackbarId);

      if (response.status === 200) {
        await selectedReport.refresh();
        showSnackbar(SNACKBAR_ACTIONS.ADD_PRACTITIONER_ACTIVITIES_SUCCESS);
        return {
          success: true,
          status: 200,
          data
        };
      }
      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const createOrUpdatePractitioner = async data => {
    const submit = isPractitionerUpdating ? updatePractitioner : createPractitioner;
    const formattedPractitionerData = serializePractitioner(data);
    console.log({formattedPractitionerData});
    return submit({...formattedPractitionerData, siren: selectedProject.siren});
  };

  const submitPractitioner = () => {
    return practitionerFormMethods.handleSubmit(async data => {
      const res = await createOrUpdatePractitioner({...data, ...(isPractitionerUpdating ? {practitionerId: selectedPractitionerId} : {})});
      if (res.success) {
        setSelectedPractitionerId(res.practitioner_id);
        goToPractitionerActivity();
      }
    })();
  };

  useEffect(() => {
    //  if (project && import.meta.env.VITE_COMPANY_NAME === 'emmai') { // TODO Uncomment before merge ?
    if (selectedProject) {
      // eslint-disable-next-line no-use-before-define
      getEmmaiData(selectedProject.siren);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (!isPractitionerUpdating) {
      practitionerFormMethods.reset(defaultValues);
    }
  }, [isPractitionerUpdating]);

  const hasPractitioners = (emmaiData?.practitioners || []).length > 0;

  const useMemoDeps = [
    emmaiData,
    getEmmaiData,
    monthlyRestitutionModalOpen,
    selectedPractitionerId,
    emmaiRestitutionSelectedDate,
    currentForm,
    isPractitionerUpdating,
    setIsPractitionerUpdating,
    selectedPractitioner,
    createPractitioner,
    registerBeforeDateChange,
    proceedMonthChange,
    warningActivityDataWillBeLostOnMonthChangeModalOpen,
    setWarningActivityDataWillBeLostOnMonthChangeModalOpen,
    pendingDate,
    setPendingDate,
    addActivities,
    updatePractitioner,
    submitPractitioner,
    practitionerFormMethods,
    deletePractitioner,
    hasPractitioners
  ];

  const value = useMemo(
    () => ({
      emmaiData,
      getEmmaiData,
      monthlyRestitutionModalOpen,
      setMonthlyRestitutionModalOpen,
      selectedPractitionerId,
      setSelectedPractitionerId,
      emmaiRestitutionSelectedDate,
      formattedEmmaiRestitutionSelectedDate: dayjs(emmaiRestitutionSelectedDate).format('YYYY-MM-DD'),
      setEmmaiRestitutionSelectedDate,
      currentForm,
      setCurrentForm,
      isPractitionerUpdating,
      setIsPractitionerUpdating,
      selectedPractitioner,
      createPractitioner,
      registerBeforeDateChange,
      proceedMonthChange,
      warningActivityDataWillBeLostOnMonthChangeModalOpen,
      setWarningActivityDataWillBeLostOnMonthChangeModalOpen,
      pendingDate,
      setPendingDate,
      addActivities,
      updatePractitioner,
      submitPractitioner,
      practitionerFormMethods,
      deletePractitioner,
      hasPractitioners
    }),
    useMemoDeps
  );

  return <EmmaiContext.Provider value={value}>{children}</EmmaiContext.Provider>;
};

EmmaiProvider.propTypes = {
  children: oneOfType([node, func]).isRequired
};

export default EmmaiProvider;
